import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import api from "../../api";

import Txt from "../../components/Text";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";
import {
  Calendar1,
  CalendarCog,
  Grid,
  List,
  ListTree,
  MapPin,
  Package,
  PackageCheck,
  PackageMinus,
  PackageX,
  Pencil,
  Search,
  Syringe,
  Table,
  Trash,
  X,
} from "lucide-react";
import formatDate from "../../utils/formatDate";
import CustomSelect from "../../components/CustomSelect";
import { ca, fr } from "zod/v4/locales";
import DateInput from "../../components/form/DateInput";
import AddRecordModal from "../../components/private/materials/AddRecordModal";
import EditRecordModal from "../../components/private/materials/EditRecordModal";
import { useDispatch } from "react-redux";
import { apiRequest, apiSuccess } from "../../redux/api/apiActions";
import errorChecker from "../../utils/errorChecker";
import {
  clearConfirmationAlerts,
  hideAlert,
  showAlert,
} from "../../redux/alert/alertActions";

const Text = ({ children, className, page = "TOOLS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const statusConfig = {
  "In Stock": {
    dot: "bg-emerald-400",
    badge: "bg-emerald-900/60 text-emerald-300 border border-emerald-700",
    icon: "✓",
  },
  "Low Stock": {
    dot: "bg-amber-400",
    badge: "bg-amber-900/60 text-amber-300 border border-amber-700",
    icon: "⚠",
  },
  "Out of Stock": {
    dot: "bg-red-500",
    badge: "bg-red-900/60 text-red-300 border border-red-700",
    icon: "✕",
  },
};

const categoryColors = {
  "Wound Care": "text-blue-400 bg-blue-900/80",
  PPE: "text-purple-400 bg-purple-900/80",
  "IV Supplies": "text-green-400 bg-green-900/80",
  Medications: "text-pink-400 bg-pink-900/80",
  Catheters: "text-orange-400 bg-orange-900/80",
  "Injection Supplies": "text-yellow-400 bg-yellow-900/80",
  Disinfectants: "text-cyan-400 bg-cyan-900/80",
  Supplies: "text-indigo-400 bg-indigo-900/80",
  "Surgical Supplies": "text-red-400 bg-red-900/80",
};

const conditionColor = (v) =>
  v >= 85 ? "bg-emerald-500" : v >= 60 ? "bg-amber-400" : "bg-red-500";

export default function ClinicMaterials() {
  const updateMaterialKey = "updateMaterial";
  const createMaterialKey = "createMaterial";
  const loadMaterialKey = "loadMaterials";
  const deleteMaterialKey = `deleteMaterial`;
  const { requests } = useSelector((state) => state.api);
  const materialsLoading = requests[loadMaterialKey]?.loading;
  const deleteLoading = requests[deleteMaterialKey]?.loading;

  const newMaterial = requests[createMaterialKey]?.success;
  const updatedMaterial = requests[updateMaterialKey]?.success;
  const deletedMaterial = requests[deleteMaterialKey]?.success;

  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );

  // Materials data state
  const [materials, setMaterials] = useState([]);
  const [error, setError] = useState(null);

  // Filter and search state
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  const [view, setView] = useState("grid");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Fetch materials from API using useCallback
  const fetchMaterials = useCallback(async () => {
    try {
      dispatch(apiRequest("GET", loadMaterialKey));
      setError(null);

      const params = {};

      // Add status filter (exclude "All")
      if (filterStatus !== "All") {
        params.status = filterStatus;
      }

      // Add search parameter
      if (search.trim()) {
        params.search = search.trim();
      }

      params.page = 1;
      params.limit = 100;

      const response = await api.get(`/materials/clinic/`, { params });
      dispatch(
        apiSuccess("GET", loadMaterialKey, "Fetched materials successfully"),
      );
      if (response.data && response.data.data) {
        setMaterials(response.data.data);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch materials");
      const errorMessage =
        error?.response?.data?.error ||
        "Erreur lors de la récupération des données.";
      const type = error?.response?.data?.type || null;
      errorChecker(errorMessage, type, dispatch, errorMessage);
    }
  }, [filterStatus, search]);

  // Fetch materials when search or filterStatus changes
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // Refresh materials when new or updated material is created
  useEffect(() => {
    if (newMaterial || updatedMaterial || deletedMaterial) {
      fetchMaterials();
    }
  }, [newMaterial, updatedMaterial, deletedMaterial, fetchMaterials]);

  const handleDelete = (element) => {
    dispatch(clearConfirmationAlerts());
    dispatch(
      showAlert({
        id: `delete-confirmation-${element.id}`,
        type: "confirmation",
        title: textTranslator("Delete Material", "TOOLS"),
        message: `${textTranslator(
          "Are you sure you want to delete material",
          "TOOLS",
        )} ${element.id}? ${textTranslator(
          "This action cannot be undone.",
          "TOOLS",
        )}`,
        onCancel: cancelDelete,
        onConfirm: () => confirmDelete(element),
      }),
    );
  };

  const confirmDelete = (material) => {
    dispatch(apiRequest("DELETE", deleteMaterialKey));
    api
      .delete(`materials/${material.id}`)
      .then((res) => {
        dispatch(
          apiSuccess(
            "DELETE",
            deleteMaterialKey,
            "Material deleted successfully",
          ),
        );
        setSelectedItem(null);
        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Material Deleted", "TOOLS"),
            message: `${textTranslator("Material", "TOOLS")} (${
              material.id
            }) ${textTranslator("Material deleted successfully", "TOOLS")}`,
          }),
        );
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error ||
          "Erreur lors de la récupération des données.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  };

  const cancelDelete = (id) => {
    dispatch(hideAlert(id));
  };

  // Theme colors based on dark mode
  const theme = {
    bg: darkMode ? "!bg-gray-800" : "!bg-white",
    cardBg: darkMode ? "!bg-slate-900" : "!bg-slate-50",
    cardBgSecondary: darkMode ? "!bg-slate-800/60" : "!bg-slate-200/60",
    border: darkMode ? "!border-slate-800" : "!border-slate-300",
    borderLight: darkMode ? "!border-slate-700" : "!border-slate-200",
    text: darkMode ? "text-white" : "text-slate-900",
    textSecondary: darkMode ? "text-slate-400" : "text-slate-600",
    textTertiary: darkMode ? "text-slate-500" : "text-slate-500",
    inputBg: darkMode ? "!bg-slate-800/70" : "!bg-slate-100",
    inputBorder: darkMode ? "!border-slate-700" : "!border-slate-300",
    hoverBg: darkMode ? "hover:bg-slate-800/40" : "hover:bg-slate-100/40",
    buttonSecondary: darkMode ? "!bg-slate-800" : "!bg-slate-200",
    modalOverlay: darkMode ? "!bg-slate-950/80" : "!bg-white/80",
  };

  const getLocalizedName = (nameObj) => {
    if (typeof nameObj === "string") return nameObj;
    return nameObj[defaultLanguage] || nameObj.en;
  };

  const getLocalizedCategory = (categoryObj) => {
    if (typeof categoryObj === "string") return categoryObj;
    return (
      categoryObj.name?.[defaultLanguage] ||
      categoryObj.name?.en ||
      categoryObj.value
    );
  };

  // Use materials from API (already filtered by backend based on search and status)
  const filtered = materials;

  const totals = {
    total: materials.length,
    inStock: materials.filter((m) => m.status === "In Stock").length,
    lowStock: materials.filter((m) => m.status === "Low Stock").length,
    outOfStock: materials.filter((m) => m.status === "Out of Stock").length,
  };

  return (
    <div
      className={arClass(
        `min-h-screen ${theme.bg} transition-colors duration-300 rounded-md`,
      )}
    >
      <main className={arClass(`relative mx-auto px-4 sm:px-6 py-8`)}>
        {/* Page title */}
        <div className={arClass("mb-8")}>
          <div
            className={arClass(
              "flex justify-between items-center max-sm:flex-col max-sm:gap-4 max-sm:items-start",
            )}
          >
            <div>
              <h2
                className={arClass(
                  `text-3xl font-extrabold tracking-tight ${theme.text}`,
                )}
              >
                <Text>Materials & Inventory</Text>
              </h2>
              <p className={arClass(`${theme.textSecondary} mt-1`)}>
                <Text>
                  Manage, track, and audit all clinic materials & supplies.
                </Text>
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className={arClass(
                `px-4 py-3 ${darkMode ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-emerald-600 hover:bg-emerald-700 text-white"} font-bold rounded-xl text-sm transition-colors flex items-center gap-2`,
              )}
            >
              <Package className="w-4 h-4" /> <Text>Add Material</Text>
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className={arClass("grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8")}>
          {[
            {
              label: "Total Materials",
              value: totals.total,
              color: darkMode
                ? "from-cyan-500/20 to-cyan-500/5"
                : "from-cyan-100 to-cyan-50",
              border: darkMode ? "border-cyan-500/30" : "border-cyan-300/50",
              text: darkMode ? "text-cyan-400" : "text-cyan-600",
              icon: <Package className="max-sm:w-5 max-sm:h-5" />,
            },
            {
              label: "In Stock",
              value: totals.inStock,
              color: darkMode
                ? "from-emerald-500/20 to-emerald-500/5"
                : "from-emerald-100 to-emerald-50",
              border: darkMode
                ? "border-emerald-500/30"
                : "border-emerald-300/50",
              text: darkMode ? "text-emerald-400" : "text-emerald-600",
              icon: <PackageCheck className="max-sm:w-5 max-sm:h-5" />,
            },
            {
              label: "Low Stock",
              value: totals.lowStock,
              color: darkMode
                ? "from-amber-500/20 to-amber-500/5"
                : "from-amber-100 to-amber-50",
              border: darkMode ? "border-amber-500/30" : "border-amber-300/50",
              text: darkMode ? "text-amber-400" : "text-amber-600",
              icon: <PackageMinus className="max-sm:w-5 max-sm:h-5" />,
            },
            {
              label: "Out of Stock",
              value: totals.outOfStock,
              color: darkMode
                ? "from-red-500/20 to-red-500/5"
                : "from-red-100 to-red-50",
              border: darkMode ? "border-red-500/30" : "border-red-300/50",
              text: darkMode ? "text-red-400" : "text-red-600",
              icon: <PackageX className="max-sm:w-5 max-sm:h-5" />,
            },
          ].map((s, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br ${s.color} border ${s.border} rounded-2xl p-5`}
            >
              <div className={arClass("flex items-start justify-between")}>
                <div>
                  <p className={arClass(`text-xs ${theme.textSecondary} mb-1`)}>
                    <Text>{s.label}</Text>
                  </p>
                  <p className={arClass(`${s.text} text-4xl font-black`)}>
                    {s.value}
                  </p>
                </div>
                <span className="text-2xl opacity-60">{s.icon}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={arClass("flex flex-col md:flex-row gap-3 mb-6")}>
          <div className={arClass("relative flex-1")}>
            <Search
              className={
                defaultLanguage === "ar"
                  ? `absolute right-3 top-1/2 -translate-y-1/2 ${theme.textSecondary} text-sm`
                  : `absolute left-3 top-1/2 -translate-y-1/2 ${theme.textSecondary} text-sm`
              }
            />
            <input
              type="text"
              placeholder={textTranslator(
                "Search materials, category, location...",
                "TOOLS",
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl ${defaultLanguage === "ar" ? "pr-9 pl-4 [direction:rtl] [text-align:right]" : "pl-9 pr-4"} py-3 text-sm ${theme.text} placeholder-${darkMode ? "slate-500" : "slate-400"} focus:outline-none focus:border-cyan-500 transition-colors`}
            />
          </div>
          <div className={arClass("flex gap-2 max-sm:grid max-sm:grid-cols-2")}>
            {["All", "In Stock", "Low Stock", "Out of Stock"].map((s, i) => (
              <button
                key={i}
                onClick={() => setFilterStatus(s)}
                className={arClass(
                  `px-3 py-2 rounded-xl text-xs font-semibold transition-all max-md:w-full ${
                    filterStatus === s
                      ? "bg-cyan-500 text-white"
                      : darkMode
                        ? "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                  }`,
                )}
              >
                <Text>{s === "All" ? "All" : s}</Text>
              </button>
            ))}
          </div>
          <div
            className={arClass(
              `flex gap-1 ${theme.buttonSecondary} rounded-xl p-1 w-max`,
            )}
          >
            <button
              onClick={() => setView("grid")}
              className={arClass(
                `px-3 py-1 rounded-lg text-xs transition-all flex items-center gap-1 ${view === "grid" ? "bg-cyan-500 text-white font-bold" : darkMode ? "text-slate-400" : "text-slate-600"}`,
              )}
            >
              <Grid /> <Text>Grid</Text>
            </button>
            <button
              onClick={() => setView("list")}
              className={arClass(
                `px-3 py-1 rounded-lg text-xs transition-all flex items-center gap-1 ${view === "list" ? "bg-cyan-500 text-white font-bold" : darkMode ? "text-slate-400" : "text-slate-600"}`,
              )}
            >
              <Table /> <Text>List</Text>
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div
            className={arClass(
              `mb-6 p-4 rounded-xl ${darkMode ? "bg-red-500/20 border border-red-500/50 text-red-300" : "bg-red-100 border border-red-300 text-red-700"}`,
            )}
          >
            <p className="font-semibold">
              <Text>Error</Text>: {error}
            </p>
          </div>
        )}

        {/* Loading state */}
        {materialsLoading && (
          <div className={arClass("mb-6")}>
            {view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className={arClass(
                      `${theme.cardBg} border ${theme.border} rounded-2xl overflow-hidden animate-pulse`,
                    )}
                  >
                    {/* Image skeleton */}
                    <div
                      className={arClass(
                        `h-44 ${darkMode ? "bg-slate-800" : "bg-slate-300"}`,
                      )}
                    />

                    {/* Content skeleton */}
                    <div className={arClass("p-4 space-y-4")}>
                      {/* Title skeleton */}
                      <div
                        className={arClass(
                          `h-4 ${darkMode ? "bg-slate-700" : "bg-slate-200"} rounded-full w-3/4`,
                        )}
                      />

                      {/* Location skeleton */}
                      <div
                        className={arClass(
                          `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-200"} rounded-full w-1/2`,
                        )}
                      />

                      {/* Condition bar skeleton */}
                      <div className={arClass("space-y-1")}>
                        <div
                          className={arClass(
                            `h-2 ${darkMode ? "bg-slate-700" : "bg-slate-200"} rounded-full`,
                          )}
                        />
                      </div>

                      {/* Stats grid skeleton */}
                      <div className={arClass("grid grid-cols-3 gap-2")}>
                        {[1, 2, 3].map((j, k) => (
                          <div
                            key={k}
                            className={arClass(
                              `${theme.cardBgSecondary} rounded-lg py-2 h-12`,
                            )}
                          />
                        ))}
                      </div>

                      {/* Footer skeleton */}
                      <div className={arClass("space-y-1")}>
                        <div
                          className={arClass(
                            `h-2 ${darkMode ? "bg-slate-700" : "bg-slate-200"} rounded-full w-full`,
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={arClass(
                  `${theme.cardBg} border ${theme.border} rounded-2xl overflow-hidden`,
                )}
              >
                <table className={arClass("w-full table-auto")}>
                  <thead
                    className={arClass(
                      `${darkMode ? "bg-slate-800/60" : "bg-slate-200/60"}`,
                    )}
                  >
                    <tr>
                      <th className={arClass("px-4 py-3 text-left")}>
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                          )}
                        />
                      </th>
                      <th
                        className={arClass(
                          "px-4 py-3 hidden md:table-cell text-left",
                        )}
                      >
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-32`,
                          )}
                        />
                      </th>
                      <th
                        className={arClass(
                          "px-4 py-3 hidden sm:table-cell text-left",
                        )}
                      >
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-24`,
                          )}
                        />
                      </th>
                      <th className={arClass("px-4 py-3 text-left")}>
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                          )}
                        />
                      </th>
                      <th
                        className={arClass(
                          "px-4 py-3 hidden lg:table-cell text-left",
                        )}
                      >
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-16`,
                          )}
                        />
                      </th>
                      <th
                        className={arClass(
                          "px-4 py-3 hidden xl:table-cell text-left",
                        )}
                      >
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                          )}
                        />
                      </th>
                      <th
                        className={arClass(
                          "px-4 py-3 hidden xl:table-cell text-left",
                        )}
                      >
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-24`,
                          )}
                        />
                      </th>
                      <th className={arClass("px-4 py-3 text-left")}>
                        <div
                          className={arClass(
                            `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                          )}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={arClass(`divide-y ${theme.border}`)}>
                    {[1, 2, 3, 4, 5].map((i, j) => (
                      <tr key={j} className={arClass("animate-pulse")}>
                        <td className={arClass("px-4 py-3")}>
                          <div className={arClass("flex items-center gap-3")}>
                            <div
                              className={arClass(
                                `w-10 h-10 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-lg`,
                              )}
                            />
                            <div className={arClass("flex-1 space-y-2")}>
                              <div
                                className={arClass(
                                  `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-24`,
                                )}
                              />
                              <div
                                className={arClass(
                                  `h-2 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-16`,
                                )}
                              />
                            </div>
                          </div>
                        </td>
                        <td
                          className={arClass("px-4 py-3 hidden md:table-cell")}
                        >
                          <div
                            className={arClass(
                              `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-32`,
                            )}
                          />
                        </td>
                        <td
                          className={arClass("px-4 py-3 hidden sm:table-cell")}
                        >
                          <div
                            className={arClass(
                              `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-24`,
                            )}
                          />
                        </td>
                        <td className={arClass("px-4 py-3")}>
                          <div
                            className={arClass(
                              `h-6 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-full w-20`,
                            )}
                          />
                        </td>
                        <td
                          className={arClass("px-4 py-3 hidden lg:table-cell")}
                        >
                          <div
                            className={arClass(
                              `h-1.5 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-full w-16`,
                            )}
                          />
                        </td>
                        <td
                          className={arClass("px-4 py-3 hidden xl:table-cell")}
                        >
                          <div
                            className={arClass(
                              `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                            )}
                          />
                        </td>
                        <td
                          className={arClass("px-4 py-3 hidden xl:table-cell")}
                        >
                          <div
                            className={arClass(
                              `h-3 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-24`,
                            )}
                          />
                        </td>
                        <td className={arClass("px-4 py-3")}>
                          <div
                            className={arClass(
                              `h-6 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded w-20`,
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* Results count */}
        <p className={arClass(`text-xs ${theme.textTertiary} mb-4`)}>
          {filtered.length} <Text>items found</Text>
        </p>

        {/* Empty state */}
        {!materialsLoading && filtered.length === 0 && (
          <div
            className={arClass(
              `p-12 text-center rounded-2xl border-2 border-dashed ${theme.border}`,
            )}
          >
            <Package className={arClass(`w-12 h-12 mx-auto mb-3 opacity-50`)} />
            <p className={arClass(`${theme.text} font-semibold mb-1`)}>
              <Text>No materials found</Text>
            </p>
            <p className={arClass(`${theme.textSecondary} text-sm`)}>
              <Text>Try adjusting your search or filters</Text>
            </p>
          </div>
        )}

        {/* Grid view */}
        {!materialsLoading && filtered.length > 0 && view === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item, index) => {
              const sc = statusConfig[item.status];
              const catValue =
                typeof item.category === "string"
                  ? item.category
                  : item.category.value;
              const cc =
                categoryColors[item.category.value] ||
                (darkMode
                  ? "text-slate-400 bg-slate-800"
                  : "text-slate-600 bg-slate-200");
              return (
                <div
                  key={index}
                  onClick={() => setSelectedItem(item)}
                  className={arClass(
                    `group ${theme.cardBg} border ${theme.border} rounded-2xl overflow-hidden cursor-pointer hover:border-cyan-500/50 hover:shadow-lg transition-all duration-300 ${darkMode ? "hover:shadow-cyan-500/10" : "hover:shadow-cyan-200/30"}`,
                  )}
                >
                  {/* Image */}
                  <div
                    className={arClass(
                      `relative h-44 overflow-hidden ${darkMode ? "bg-slate-800" : "bg-slate-300"}`,
                    )}
                  >
                    <img
                      src={item.image}
                      alt={getLocalizedName(item.name)}
                      className={arClass(
                        "w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100",
                      )}
                    />
                    <div
                      className={arClass(
                        `absolute inset-0 bg-gradient-to-t ${darkMode ? "from-slate-900 via-transparent to-transparent" : "from-white via-transparent to-transparent"}`,
                      )}
                    />
                    <div
                      className={
                        defaultLanguage === "ar"
                          ? "absolute top-3 right-3"
                          : "absolute top-3 left-3"
                      }
                    >
                      <span
                        className={arClass(
                          `text-xs px-2 py-0.5 rounded-full font-medium ${cc}`,
                        )}
                      >
                        {getLocalizedCategory(item.category)}
                      </span>
                    </div>
                    <div
                      className={
                        defaultLanguage === "ar"
                          ? "absolute top-3 left-3"
                          : "absolute top-3 right-3"
                      }
                    >
                      <span
                        className={arClass(
                          `text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${sc.badge}`,
                        )}
                      >
                        <span
                          className={arClass(
                            `w-1.5 h-1.5 rounded-full ${sc.dot}`,
                          )}
                        />
                        <Text>{item.status}</Text>
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={arClass(`p-4`)}>
                    <h3 className={`font-bold ${theme.text} text-base mb-0.5`}>
                      {getLocalizedName(item.name)}
                    </h3>
                    <p
                      className={arClass(
                        `text-xs ${theme.textSecondary} mb-3 flex items-center`,
                      )}
                    >
                      <MapPin className="w-4 h-4" /> {item.location} · #
                      {item.serialNo}
                    </p>

                    {/* Condition bar */}
                    <div className={arClass("mb-4")}>
                      <div
                        className={arClass("flex justify-between text-xs mb-1")}
                      >
                        <span className={theme.textSecondary}>
                          <Text>Stock Level Score</Text>
                        </span>
                        <span className={`font-semibold ${theme.text}`}>
                          {item.condition}%
                        </span>
                      </div>
                      <div
                        className={arClass(
                          `h-2 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-full overflow-hidden`,
                        )}
                      >
                        <div
                          className={`h-full rounded-full ${conditionColor(item.condition)} transition-all`}
                          style={{ width: `${item.condition}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div
                      className={arClass("grid grid-cols-3 gap-2 text-center")}
                    >
                      {[
                        { label: "Total", value: item.quantity },
                        { label: "In Use", value: item.inUse },
                        {
                          label: "Available",
                          value: item.quantity - item.inUse,
                        },
                      ].map((s, index) => (
                        <div
                          key={index}
                          className={arClass(
                            `${theme.cardBgSecondary} rounded-lg py-2`,
                          )}
                        >
                          <p className={`text-base font-bold ${theme.text}`}>
                            {s.value}
                          </p>
                          <p
                            className={arClass(`text-xs ${theme.textTertiary}`)}
                          >
                            <Text>{s.label}</Text>
                          </p>
                        </div>
                      ))}
                    </div>

                    <div
                      className={arClass(
                        `mt-3 pt-3 border-t flex flex-row max-[460px]:flex-col sm:flex-col 2xl:flex-row gap-2 ${theme.border} justify-between text-xs ${theme.textTertiary}`,
                      )}
                    >
                      <span className={arClass("flex items-center gap-1")}>
                        <Calendar1 className="w-4 h-4" />
                        <Text>Last Audited</Text> {formatDate(item.lastAudit)}
                      </span>
                      <span className={arClass("flex items-center gap-1")}>
                        <CalendarCog className="w-4 h-4" />
                        <Text>Next Audit</Text> {formatDate(item.nextAudit)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List view */}
        {!materialsLoading && filtered.length > 0 && view === "list" && (
          <div
            className={`${theme.cardBg} border ${theme.border} rounded-2xl overflow-hidden`}
          >
            <table className={arClass(`w-full table-auto`)}>
              <thead
                className={arClass(
                  `${darkMode ? "bg-slate-800/60" : "bg-slate-200/60"} ${theme.textSecondary} uppercase tracking-wider`,
                )}
              >
                <tr className={arClass(`p-1`)}>
                  <th className={arClass(`px-4 py-3`)}>
                    <Text>Equipment</Text>
                  </th>
                  <th className={arClass(`px-4 py-3 hidden md:table-cell`)}>
                    <Text>Category</Text>
                  </th>
                  <th className={arClass(`px-4 py-3 hidden sm:table-cell`)}>
                    <Text>Location</Text>
                  </th>
                  <th className={arClass(`px-4 py-3`)}>
                    <Text>Status</Text>
                  </th>
                  <th className={arClass(`px-4 py-3 hidden lg:table-cell`)}>
                    <Text>Condition</Text>
                  </th>
                  <th className={arClass(`px-4 py-3 hidden xl:table-cell`)}>
                    <Text>In Use</Text>
                  </th>
                  <th className={arClass(`px-4 py-3 hidden xl:table-cell`)}>
                    <Text>Next Audit</Text>
                  </th>
                  <th className={arClass(`px-4 py-3`)}>
                    <Text>Action</Text>
                  </th>
                </tr>
              </thead>

              <tbody className={arClass(`divide-y ${theme.border}`)}>
                {filtered.map((item, index) => {
                  const sc = statusConfig[item.status];
                  const catValue =
                    typeof item.category === "string"
                      ? item.category
                      : item.category.value;
                  const cc =
                    categoryColors[catValue] ||
                    (darkMode
                      ? "text-slate-400 bg-slate-800"
                      : "text-slate-600 bg-slate-200");
                  return (
                    <tr key={index} className={theme.hoverBg}>
                      <td className={arClass("px-4 py-3")}>
                        <div className={arClass("flex items-center gap-3")}>
                          <img
                            src={item.image}
                            alt={getLocalizedName(item.name)}
                            className={`!w-10 !h-10 !max-w-full rounded-lg object-cover ${darkMode ? "bg-slate-700" : "bg-slate-300"}`}
                          />
                          <div>
                            <p className={`font-semibold ${theme.text}`}>
                              {getLocalizedName(item.name)}
                            </p>
                            <p className={`text-xs ${theme.textSecondary}`}>
                              #{item.serialNo}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className={arClass("px-4 py-3 hidden md:table-cell")}>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${cc}`}
                        >
                          {getLocalizedCategory(item.category)}
                        </span>
                      </td>
                      <td
                        className={arClass(
                          `px-4 py-3 hidden sm:table-cell ${theme.textSecondary} text-xs`,
                        )}
                      >
                        {item.location}
                      </td>
                      <td className={arClass("px-4 py-3")}>
                        <span
                          className={arClass(
                            `text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${sc.badge}`,
                          )}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}
                          />
                          <Text>{item.status}</Text>
                        </span>
                      </td>
                      <td className={arClass("px-4 py-3 hidden lg:table-cell")}>
                        <div className={arClass("flex items-center gap-2")}>
                          <div
                            className={arClass(
                              `flex-1 h-1.5 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-full overflow-hidden w-20`,
                            )}
                          >
                            <div
                              className={arClass(
                                `h-full rounded-full ${conditionColor(item.condition)}`,
                              )}
                              style={{ width: `${item.condition}%` }}
                            />
                          </div>
                          <span
                            className={`text-xs ${darkMode ? "text-slate-300" : "text-slate-700"}`}
                          >
                            {item.condition}%
                          </span>
                        </div>
                      </td>
                      <td
                        className={arClass(
                          `px-4 py-3 hidden xl:table-cell ${darkMode ? "text-slate-300" : "text-slate-700"} text-xs`,
                        )}
                      >
                        {item.inUse}/{item.quantity}
                      </td>
                      <td
                        className={arClass(
                          `px-4 py-3 hidden xl:table-cell ${theme.textSecondary} text-xs`,
                        )}
                      >
                        {item.nextAudit}
                      </td>
                      <td className={arClass("px-4 py-3")}>
                        <button
                          onClick={() => setSelectedItem(item)}
                          className={`text-xs ${darkMode ? "bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20" : "bg-cyan-100 border border-cyan-400 text-cyan-700 hover:bg-cyan-200"} px-2 py-1 rounded-lg transition-colors`}
                        >
                          <Text>View Details</Text>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedItem && (
        <div
          className={arClass(
            `fixed inset-0 z-[1000] flex items-center justify-center p-4`,
          )}
          onClick={() => setSelectedItem(null)}
        >
          <div
            className={`absolute inset-0 ${theme.modalOverlay} backdrop-blur-sm`}
          />
          <div
            className={arClass(
              `relative ${theme.cardBg} border ${theme.border} rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl`,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={arClass(
                `relative h-52 overflow-hidden ${darkMode ? "bg-slate-800" : "bg-slate-300"}`,
              )}
            >
              <img
                src={selectedItem.image}
                alt={getLocalizedName(selectedItem.name)}
                className="w-full h-full object-cover opacity-90"
              />
              <div
                className={arClass(
                  `absolute inset-0 bg-gradient-to-t ${darkMode ? "from-slate-900 via-slate-900/30 to-transparent" : "from-white via-white/30 to-transparent"}`,
                )}
              />
              <button
                onClick={() => setSelectedItem(null)}
                className={arClass(
                  `absolute top-3 right-3 w-8 h-8 ${darkMode ? "bg-slate-900/70 text-slate-300 hover:text-white hover:bg-slate-800" : "bg-white/70 text-slate-700 hover:text-slate-900 hover:bg-white"} rounded-full flex items-center justify-center transition-colors text-sm`,
                )}
              >
                <X className="w-4 h-4" />
              </button>
              <div className={arClass("absolute bottom-4 left-5 right-5")}>
                <h3 className={arClass(`text-2xl font-black ${theme.text}`)}>
                  {getLocalizedName(selectedItem.name)}
                </h3>
                <p className={theme.textSecondary}>{selectedItem.serialNo}</p>
              </div>
            </div>

            <div className={`p-5 grid grid-cols-2 gap-4`}>
              {[
                {
                  label: "Status",
                  value: textTranslator(selectedItem.status, "TOOLS"),
                  badge: statusConfig[selectedItem.status].badge,
                },
                {
                  label: "Category",
                  value: getLocalizedCategory(selectedItem.category),
                },
                { label: "Location", value: selectedItem.location },
                {
                  label: "Stock Level Score",
                  value: `${selectedItem.condition}%`,
                },
                { label: "Total Units", value: selectedItem.quantity },
                { label: "Currently In Use", value: selectedItem.inUse },
                {
                  label: "Available",
                  value: selectedItem.quantity - selectedItem.inUse,
                },
                {
                  label: "Last Audited",
                  value: formatDate(selectedItem.lastAudit),
                },
                {
                  label: "Next Audit",
                  value: formatDate(selectedItem.nextAudit),
                },
              ].map((f, index) => (
                <div
                  key={index}
                  className={arClass(f.label === "Status" ? "" : "")}
                >
                  <p
                    className={arClass(`text-xs ${theme.textSecondary} mb-0.5`)}
                  >
                    <Text>{f.label}</Text>
                  </p>
                  {f.badge ? (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${f.badge}`}
                    >
                      {f.value}
                    </span>
                  ) : (
                    <p className={`text-sm font-semibold ${theme.text}`}>
                      {f.value}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className={`px-5 mb-5`}>
              <div className={arClass("flex justify-between text-xs mb-1")}>
                <span className={theme.textSecondary}>
                  <Text>Stock Level Score</Text>
                </span>
                <span className={`font-bold ${theme.text}`}>
                  {selectedItem.condition}%
                </span>
              </div>
              <div
                className={arClass(
                  `h-2 ${darkMode ? "bg-slate-700" : "bg-slate-300"} rounded-full overflow-hidden`,
                )}
              >
                <div
                  className={arClass(
                    `h-full rounded-full ${conditionColor(selectedItem.condition)} transition-all`,
                  )}
                  style={{ width: `${selectedItem.condition}%` }}
                />
              </div>
            </div>

            <div className={arClass(`flex gap-3 px-5 mb-5`)}>
              <button
                onClick={() => {
                  setEditingItem(selectedItem);
                  setShowEditModal(true);
                  setSelectedItem(null);
                }}
                className={arClass(
                  `flex-1 disabled:opacity-50 disabled:cursor-not-all bg-cyan-500 ${darkMode ? "hover:bg-cyan-400" : "hover:bg-cyan-600"} ${darkMode ? "text-slate-950" : "text-white"} font-bold py-3 rounded-xl text-sm transition-colors`,
                )}
                disabled={deleteLoading}
              >
                <Pencil className="inline mr-2 w-4 h-4" />{" "}
                <Text>Edit Record</Text>
              </button>
              <button
                onClick={() => handleDelete(selectedItem)}
                className={arClass(
                  `flex-1 disabled:opacity-50 disabled:cursor-not-all  ${darkMode ? "hover:bg-red-700 text-red-300 bg-red-500" : "hover:bg-red-300 text-red-700 bg-red-200"} font-semibold py-3 rounded-xl text-sm transition-colors`,
                )}
                disabled={deleteLoading}
              >
                <Trash className="inline mr-2 w-4 h-4" />{" "}
                <Text>{deleteLoading ? "Deleting..." : "Delete Record"}</Text>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Record Modal */}
      {showAddModal && (
        <AddRecordModal
          darkMode={darkMode}
          defaultLanguage={defaultLanguage}
          theme={theme}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Edit Record Modal */}
      {showEditModal && editingItem && (
        <EditRecordModal
          darkMode={darkMode}
          defaultLanguage={defaultLanguage}
          theme={theme}
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
}
